import React, { ReactNode, useEffect } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { QUIZ_CHOICE } from '../itemIds';
import styled from '@xstyled/styled-components';
import {
  QuizProvider,
  useQuizStore
} from '~/training/sequence/templates/items/quiz/QuizStore';
import { FieldSetElement } from '~/training/sequence/templates/items';
import { t } from 'ttag';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';

import { useEditorStore } from '~/shell/Store';

import { select } from '~/lib/select';
import LightButton from '~/editor/buttons/LightButton';

interface Props {
  variant?: string;
  title?: string;
  length?: number;
  multiSelect?: number;
  maxLength?: number | undefined | null;
  disableButtons?: boolean;
  weNeedToUseOwnFeedback?: boolean;
}

const FlexBlock = styled.divBox`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  column-gap: sm;

  > button {
    flex: 1;
    margin-bottom: md;
  }
  > button:focus {
    outline: 0;
  }
  &.list > button {
    margin-left: 0px;
    margin-right: 0px;
    min-width: 100%;
  }
  &.list > .feedback {
    width: 100%;
    margin-top: 10px;
    margin-left: auto;
    margin-right: auto;
  }

  > div {
    padding-bottom: md;
  }
`;

const Feedback = styled.divBox`
  color: #000000;
  background: #fcf8e9;
  border-radius: 10px;
  padding: 15px;
  border: 1px solid grey;
  width: 100%;
  margin-bottom: 10px;

  a {
    color: var(--color-accent-dk);
    text-decoration: underline;
  }
`;

export const FeedbackBlock = ({
  quizId: propQuizId,
  ...args
}: {
  quizId?: string;
  [key: string]: any;
}) => {
  const { getQuizFeedback } = useQuizStore();
  const quizFeedback = getQuizFeedback(propQuizId);

  return quizFeedback ? (
    <Feedback
      className={'feedback'}
      {...args}
      dangerouslySetInnerHTML={{ __html: quizFeedback }}
    />
  ) : null;
};

export type QuizChoiceDefinition = ItemDefinition<typeof QUIZ_CHOICE, Props>;

const sequenceSelector = (s: SequenceStore) => select(s, 'blockers');

export class QuizChoice extends Item<typeof QUIZ_CHOICE, Props> {
  protected render({
    id,
    children,
    variant,
    title,
    length,
    multiSelect,
    maxLength = null,
    disableButtons = false,
    weNeedToUseOwnFeedback = false
  }: RenderProps<Props>) {
    const { setQuizFeedback, setQuizId, setWeNeedToUseOwnFeedback } = useQuizStore();
    const { blockers } = useSequenceStore(sequenceSelector);
    const textarea = document.createElement('textarea');
    const tempElement = document.createElement('div');

    useEffect(() => {
      setQuizId(id);
    }, [id]);

    useEffect(() => {
      setWeNeedToUseOwnFeedback(weNeedToUseOwnFeedback);
    }, [weNeedToUseOwnFeedback]);

    const { contentEditable, sequence, addSubItem } = useEditorStore();

    textarea.innerHTML = title || t`Choose the correct answer(s)`;
    tempElement.innerHTML = textarea.value;

    const txtTitle = tempElement?.textContent
      ?.replace(/&quot;/g, '')
      ?.replace('"', '')
      ?.replace('"', '')
      ?.replace('?', '? ')
      ?.trim();

    //it is for accessibility
    const onKeyDown = (e: React.KeyboardEvent) => {
      const active = document?.activeElement;

      if (
        active?.previousElementSibling &&
        (e.key == 'ArrowLeft' || e.key == 'ArrowUp')
      ) {
        (active.previousElementSibling as HTMLElement).focus();
      }

      if (
        active?.nextElementSibling &&
        (e.key == 'ArrowRight' || e.key == 'ArrowDown')
      ) {
        (active.nextElementSibling as HTMLElement).focus();
      }
    };

    const AddNewItem = () => {
      const tempID = String(id).split('.').slice(0, -1).join('.');

      let question = sequence.items.find(
        (item: { id: string; type: string }) => item.type === 'QUESTION'
      );

      if (tempID) {
        const tempQuestion = sequence.items.find(
          (item: { id: string; type: string }) =>
            item.id == tempID && item.type == 'QUESTION'
        );

        if (tempQuestion) {
          question = tempQuestion;
        }
      }

      if (question && question.items && question.items.length > 0) {
        const lastItem = question.items[question.items.length - 1];

        if (lastItem) {
          const newItem = {
            ...lastItem,
            id: `${Date.now()}`,
            position: question.items.length + 1
          };
          newItem.value = 'Type Here';

          addSubItem({
            sequenceId: sequence.id,
            data: {
              type: newItem.type,
              title: newItem.value,
              value: newItem.value,
              position: question.items.length + 1,
              correct: newItem.correct,
              parent_id: question.id
            },
            newItem: newItem
          });
        }
      }
    };

    function isEmpty(obj: ReactNode) {
      const array = React.Children.toArray(obj);
      return array.length === 0;
    }

    useEffect(() => {
      // ENG-817: Multimedia4_Quiz template issues
      // setQuizFeedback('');
      setQuizFeedback('', id);
    }, [id]);

    return (
      <QuizProvider quizId={id}>
        <FieldSetElement>
          <FlexBlock className={variant} onKeyDown={onKeyDown}>
            {children}
            {contentEditable &&
              !disableButtons &&
              !isEmpty(children) &&
              children &&
              (maxLength === null ||
                maxLength > React.Children.toArray(children).length) && (
                <LightButton
                  style={{
                    minWidth: '140px',
                    maxWidth: '140px',
                    marginLeft: '15px'
                  }}
                  onClick={AddNewItem}
                >
                  Add Item
                </LightButton>
              )}
            <FeedbackBlock quizId={id} />
            {!contentEditable &&
            length !== undefined &&
            length > 2 &&
            multiSelect &&
            multiSelect > 1 &&
            blockers &&
            blockers.length >= 1 &&
            blockers.length != multiSelect ? (
              <Feedback>{t`Select all the correct answers`}.</Feedback>
            ) : null}
          </FlexBlock>
          <legend className="sr-only" aria-label={txtTitle}>
            {txtTitle}
          </legend>
        </FieldSetElement>
      </QuizProvider>
    );
  }
}

registry[QUIZ_CHOICE] = QuizChoice;
